import type { ReactNode } from 'react';

import { EmbeddedSwitcher } from '../../../chrome/entity/EntitySwitcher';
import { count } from '../../../chrome/measure/format';
import { getLocale } from '../../../i18n';
import { Card, CardContent } from '../../../ui/card';
import { Separator } from '../../../ui/separator';
import type { PostRow } from '../api/schemas';
import {
  KindBadge,
  moment,
  postAge,
  PostAvatar,
  StoryExpiry,
  StoryFrame,
} from '../publications/cells';
import { SharedMediaSection } from './SharedMedia';
import { pub, pubPlural } from './messages';

/** "@handle", however the wire spelled it. */
function atHandle(handle: string | null): string | null {
  return handle === null ? null : `@${handle.replace(/^@/, '')}`;
}

/** The breadcrumb's leaf: whose story, and nothing the panel already says. */
export function storyTitle(post: PostRow): string {
  return pub('story.title', {
    handle: atHandle(post.handle) ?? post.label ?? pub('post.authorAbsent'),
  });
}

function captures(post: PostRow): string[] {
  if (post.captures !== null) return post.captures;
  return post.captured_at === null ? [] : [post.captured_at];
}

function duration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

function SectionLabel(props: { children: ReactNode }): ReactNode {
  return <h2 className="text-caption text-muted-foreground font-medium">{props.children}</h2>;
}

/** The story's 24 hours as one track: the stretch between its first and last
 *  capture filled, each capture a tick, a line at the present while it is up.
 *  The ends are named, not dated: the author row already carries both times. */
function OnAir(props: { post: PostRow; seen: string[] }): ReactNode {
  const { post, seen } = props;
  const locale = getLocale();
  const first = seen[0];
  const last = seen[seen.length - 1];
  const start = new Date(post.published_at).getTime();
  const end = post.expires_at === null ? start : new Date(post.expires_at).getTime();
  const at = (iso: string): number =>
    end === start
      ? 0
      : Math.min(100, Math.max(0, ((new Date(iso).getTime() - start) / (end - start)) * 100));
  const now = new Date().toISOString();
  const live = post.expires_at !== null && now < post.expires_at;
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{pub('story.onAir')}</SectionLabel>
      {post.expires_at !== null && (
        <div className="flex flex-col gap-1.5">
          <div
            role="img"
            aria-label={pub('story.timeline', {
              from: moment(post.published_at),
              to: moment(post.expires_at),
              count: count(seen.length, locale),
            })}
            className="bg-secondary relative h-2.5 rounded-full"
          >
            {first !== undefined && last !== undefined && (
              <div
                className="bg-primary/30 absolute inset-y-0 rounded-full"
                style={{ left: `${at(first)}%`, width: `${at(last) - at(first)}%` }}
              />
            )}
            {seen.map((iso) => (
              <div
                key={iso}
                className="bg-primary absolute inset-y-0 w-1 -translate-x-1/2 rounded-full"
                style={{ left: `${at(iso)}%` }}
              />
            ))}
            {live && (
              <div
                className="bg-foreground absolute -inset-y-1 w-0.5 -translate-x-1/2"
                style={{ left: `${at(now)}%` }}
              />
            )}
          </div>
          <div className="text-caption text-muted-foreground flex justify-between gap-2">
            <span>{pub('story.from')}</span>
            <span>{pub('story.to')}</span>
          </div>
        </div>
      )}
      {first !== undefined && last !== undefined && (
        <p className="text-body-sm">
          {seen.length === 1
            ? pub('story.summary.one', { time: moment(first) })
            : pubPlural('story.summary.other', seen.length, {
                duration: duration(new Date(last).getTime() - new Date(first).getTime()),
                count: count(seen.length, locale),
              })}
        </p>
      )}
    </section>
  );
}

/**
 * A story on its own page: its 9:16 frame beside one panel that says each fact
 * once. The route returns no url, caption, figures, metrics, composition,
 * terms or comments for one, so `Publication.tsx` renders this instead of
 * `PostCard` and its sections.
 */
export function StoryCard(props: {
  post: PostRow;
  campaignId: string;
  tenantId: string;
}): ReactNode {
  const { post } = props;
  const seen = captures(post);
  const handle = atHandle(post.handle);
  return (
    <div className="grid items-start gap-4 md:grid-cols-story-aside-md lg:grid-cols-story-aside-lg">
      <StoryFrame
        post={post}
        controls
        className="mx-auto w-full max-w-72 rounded-xl md:max-w-none"
      />
      <Card>
        <CardContent className="flex flex-col gap-5">
          <div className="flex items-start gap-3">
            <PostAvatar post={post} />
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-title-md min-w-0">
                  <EmbeddedSwitcher>
                    <span className="min-w-0 truncate">
                      {post.label ?? handle ?? pub('post.authorAbsent')}
                    </span>
                  </EmbeddedSwitcher>
                </h1>
                <KindBadge post={post} />
                {post.expires_at !== null && <StoryExpiry expiresAt={post.expires_at} />}
              </div>
              <p className="text-caption text-muted-foreground">
                {[
                  post.label !== null ? handle : null,
                  pub('story.published', {
                    ago: postAge(post.published_at, getLocale()),
                    date: moment(post.published_at),
                  }),
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
              </p>
            </div>
          </div>
          <Separator />
          <OnAir post={post} seen={seen} />
          <Separator />
          <SharedMediaSection
            campaignId={props.campaignId}
            tenantId={props.tenantId}
            postId={post.post_id}
          />
        </CardContent>
      </Card>
    </div>
  );
}
